// https://projects.invisionapp.com/d/main#/console/19025613/403443135/preview

// stacked bar chart implementation informed by
// app/static/frontend/widgets/components/visualization/Bar.jsx
// https://observablehq.com/@d3/stacked-bar-chart
// https://bl.ocks.org/mstanaland/6100713
// https://bl.ocks.org/LemoNode/5a64865728c6059ed89388b5f83d6b67
// https://bl.ocks.org/KingOfCramers/04dcd9742a2be13d99db5f7a7480b4ca

// 100% stacked bar chart implementation informed by
// https://observablehq.com/@d3/stacked-normalized-horizontal-bar
// https://www.d3-graph-gallery.com/graph/barplot_stacked_percent.html
// https://gist.github.com/mbostock/3886394

// stacked horizontal bar chart implementation informed by
// https://observablehq.com/@d3/stacked-horizontal-bar-chart
// https://bl.ocks.org/Andrew-Reid/0aedd5f3fb8b099e3e10690bd38bd458
// https://bl.ocks.org/mikemaieli/b686c42530143717b0d79fea2345d4a1
// https://bl.ocks.org/d3noob/c506ac45617cf9ed39337f99f8511218

import React, { useRef, useState } from "react"
import PropTypes from "prop-types"

// components
import { VisualizationBarWidget as StyledVisualizationBarWidget, Legend } from "QuorumDesign"

// d3 renderers
import {
    useGetXDomain,
    useGetXRangeRoundBands,
    useGetYDomain,
    useRenderSvg,
    useRenderXAxis,
    useRenderYAxis,
    useXRenderer,
    useYRenderer,
} from "app/static/frontend/widgets/components/visualization/renderers/sharedRenderers"
import { renderStackedBars } from "app/static/frontend/widgets/components/visualization/renderers/bar/barStackedRenderers"

// hooks
import { useRefDimensions } from "app/static/frontend/widgets/components/visualization/hooks/visualizationHooks"
import {
    getVisualizationOverrideHeight,
    getVisualizationOverrideWidth,
    getVisualizationXAxisLabelsLongestLength,
    useVisualizationMargin,
} from "app/static/frontend/widgets/components/visualization/hooks/visualizationAxesHooks"
import { useBarVisualizationData } from "app/static/frontend/widgets/components/visualization/hooks/bar/barHooks"
import { useBarStackedSeries } from "app/static/frontend/widgets/components/visualization/hooks/bar/barStackedHooks"

// constants
import dashboardConstants from "app/static/frontend/dashboards/constants"

export const BarStacked = (props) => {
    const bar = useRef(null)
    const { height, width } = useRefDimensions({
        reference: bar,
        timestamp: props.timestamp,
    })

    const data = useBarVisualizationData({
        data: props.data,
        dataSources: props.dataSources,
        groupKey: props.groupKey,
        labels: props.labels,
        quorumDataType: props.quorumDataType,
    })
    const margin = useVisualizationMargin({
        data: props.data,
        dataSources: props.dataSources,
        height,
        width,
        isHorizontal: props.isHorizontal,
        percentage: props.percentage,
        quorumDataType: props.quorumDataType,
    })
    const xAxisLabelsLongestLength = getVisualizationXAxisLabelsLongestLength({
        data: props.data,
        dataSources: props.dataSources,
        height,
        width,
        isHorizontal: props.isHorizontal,
        quorumDataType: props.quorumDataType,
    })
    const [loadAll, setLoadAll] = useState(data.length < dashboardConstants.visualizationLoadAll)
    const barData = !loadAll ? data.slice(0, dashboardConstants.visualizationLoadAll) : data
    const overrideHeight = props.isHorizontal
        ? getVisualizationOverrideHeight({
              data: barData,
              height,
          })
        : height
    const overrideWidth = props.isHorizontal
        ? width
        : getVisualizationOverrideWidth({
              data: barData,
              width,
          })

    const useRenderBarStacked = () => {
        const series = useBarStackedSeries({
            data: barData,
            groupKey: props.groupKey,
            // 100% stacked
            percentage: props.percentage,
            keys: props.keys,
            shouldOrderByDate: props.shouldOrderByDate,
            timestamp: props.timestamp,
        })

        // converts above series to
        // [
        //     "Lee (D-CA)",
        //     "Johnson (D-TX)",
        //     "Lipinski (D-IL)",
        //     "Polis (D-CO)",
        //     ...
        // ]
        const xDomainMemo = useGetXDomain({
            series,
            timestamp: props.timestamp,
        })
        const xRangeRoundBandsMemo = useGetXRangeRoundBands({
            // isHorizontal
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            margin,
            timestamp: props.timestamp,
            width: overrideWidth || width,
        })
        const xRendererMemo = useXRenderer({
            domain: xDomainMemo,
            // isHorizontal
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            rangeRoundBandsMemo: xRangeRoundBandsMemo,
            timestamp: props.timestamp,
            width: overrideWidth || width,
        })

        // returns 80, which is the max y0 value in the series above
        const yDomainMemo = useGetYDomain({
            // 100% stacked
            percentage: props.percentage,
            series,
            timestamp: props.timestamp,
        })
        const yRendererMemo = useYRenderer({
            domain: yDomainMemo,
            // necessary for the .range
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            margin,
            // 100% stacked
            percentage: props.percentage,
            timestamp: props.timestamp,
            // isHorizontal
            width: overrideWidth || width,
        })

        useRenderXAxis({
            // necessary for the translate in xAxisRenderer
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            margin,
            // isHorizontal
            percentage: props.percentage,
            reference: bar,
            timestamp: props.timestamp,
            width: overrideWidth || width,
            xAxisLabelsLongestLength,
            xRendererMemo: props.isHorizontal ? yRendererMemo : xRendererMemo,
        })

        useRenderYAxis({
            height: overrideHeight || height,
            isHorizontal: props.isHorizontal,
            margin,
            // 100% stacked
            percentage: props.percentage,
            reference: bar,
            timestamp: props.timestamp,
            // necessary for the .innerTickSize in yAxisRenderer
            width: overrideWidth || width,
            yRendererMemo: props.isHorizontal ? xRendererMemo : yRendererMemo,
        })

        renderStackedBars({
            colors: props.colors,
            dataSources: props.dataSources,
            editing: props.editing,
            editForm: props.editForm,
            height: overrideHeight || height,
            isExternal: props.isExternal,
            isHorizontal: props.isHorizontal,
            // 100% stacked
            percentage: props.percentage,
            reference: bar,
            // stacked
            series,
            // for segueing from Bar onClick to safedSearch selection in search
            showInSearch: props.showInSearch,
            timestamp: props.timestamp,
            width: overrideWidth || width,
            x: props.isHorizontal ? yRendererMemo : xRendererMemo,
            y: props.isHorizontal ? xRendererMemo : yRendererMemo,
        })
    }

    useRenderSvg({
        height,
        reference: bar,
        // BarStacked
        overrideHeight,
        // BarStacked
        overrideWidth,
        width,
    })

    useRenderBarStacked()

    return (
        <React.Fragment>
            <StyledVisualizationBarWidget
                isHorizontal={props.isHorizontal}
                loadAll={loadAll}
                loadAllOnClick={() => {
                    setLoadAll(!loadAll)
                    props.updateTimestamp()
                }}
                noHeader={props.noHeader}
                // show the "Load More"/"Show Less" button if there are more than
                // visualizationLoadAll a-axis entries in the processed Bar Visualization data
                shouldRenderLoadAll={!props.editing && data.length > dashboardConstants.visualizationLoadAll}
            >
                <svg
                    data-cy="stacked-bar-visualization-widget"
                    ref={bar}
                    // ensure that the BarStacked svg stretches to the height of the parent div
                    // this is important because we want the onResize to set correct values
                    // immediately after mount
                    style={{
                        flexGrow: 1,
                        ...((props.isHorizontal && loadAll) || overrideHeight
                            ? // svg element sets "overflow: hidden;" by default,
                              // which causes the horizontal stacked bar visualization
                              // to render erroneously in overflow mode
                              { overflow: "unset" }
                            : {}),
                    }}
                />
            </StyledVisualizationBarWidget>
            {props.colors && data && <Legend colors={props.colors} data={props.labels} />}
        </React.Fragment>
    )
}

BarStacked.defaultProps = {
    // TODO: dummy colors
    // this will eventually come from the ColorPickerField(s) component
    colors: {
        0: "#A95AA1",
        1: "#503ABD",
        2: "#85C0F9",
    },
    // TODO: dummy data
    // this will eventually come from the FieldArray (index), labels (name), and data (value) from the evaluated SafedSearch
    data: [
        {
            20186: { agg: 1, groupKeyValue: "AL Rep. Howard Sanderford (R-AL-020)" },
            20338: { agg: 1, groupKeyValue: "AL Rep. Ritchie Whorton (R-AL-022)" },
            21168: { agg: 1, groupKeyValue: "AL Rep. Randall Shedd (R-AL-011)" },
            22110: { agg: 1, groupKeyValue: "AL Rep. Laura Hall (D-AL-019)" },
        },
        {
            20186: { agg: 2, groupKeyValue: "AL Rep. Howard Sanderford (R-AL-020)" },
            20338: { agg: 1, groupKeyValue: "AL Rep. Ritchie Whorton (R-AL-022)" },
            21168: { agg: 1, groupKeyValue: "AL Rep. Randall Shedd (R-AL-011)" },
            22110: { agg: 2, groupKeyValue: "AL Rep. Laura Hall (D-AL-019)" },
            1269249: { agg: 1, groupKeyValue: "AL Rep. Neil Rafferty (D-AL-054)" },
        },
    ],
    // key to group on
    groupKey: "name",
    // keys for grouped
    keys: ["Science", "Engineering"],
}
BarStacked.propTypes = {
    colors: PropTypes.objectOf(PropTypes.string),
    // the WidgetResource widget_engine (app/widgets/api.py) returns:
    // an object if there is a single result
    // an array if there are multiple results
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    dataSources: PropTypes.array,
    editing: PropTypes.bool,
    editForm: PropTypes.bool,
    groupKey: PropTypes.string,
    isExternal: PropTypes.bool,
    isHorizontal: PropTypes.bool,
    labels: PropTypes.array,
    noHeader: PropTypes.bool,
    // whether or not we are rendering a 100% Stacked Bar component
    percentage: PropTypes.bool,
    keys: PropTypes.array,
    quorumDataType: PropTypes.object,
    shouldOrderByDate: PropTypes.bool,
    // the timestamp is used for memoization in place of props.data
    // because it is less computationally intensive to compare
    timestamp: PropTypes.number,
    updateTimestamp: PropTypes.func,

    // action creators
    showInSearch: PropTypes.func,
}

export default BarStacked
